// v2 size parity: the business app keeps Nuxt UI v2's size metrics, so a `size` prop renders as it did
// in v2. v4's sizes have different paddings, font sizes and icon sizes, and none matches v2's `lg`
// (40px tall with `text-sm`), so shifting props to another size cannot reproduce it. Every block marked
// "v2 size parity" overrides v4 theme classes; see `.agents/docs/UI_SYSTEM.md` before upgrading Nuxt UI.
const v2FieldSizeVariants = {
  xs: { base: 'px-2.5 py-1.5', leading: 'ps-2.5', trailing: 'pe-2.5' },
  sm: { base: 'text-sm/5', leadingIcon: 'size-5', trailingIcon: 'size-5' },
  md: { base: 'px-3 py-2', leading: 'ps-3', trailing: 'pe-3' },
  lg: { base: 'px-3.5 py-2.5', leading: 'ps-3.5', trailing: 'pe-3.5' },
  xl: { base: 'px-3.5 py-2.5', leading: 'ps-3.5', trailing: 'pe-3.5' },
}

const v2FieldCompoundVariants = [
  { fixed: false, size: 'sm' as const, class: 'md:text-sm' },
  { leading: true, size: 'xs' as const, class: 'ps-8' },
  { leading: true, size: 'sm' as const, class: 'ps-9' },
  { leading: true, size: 'md' as const, class: 'ps-10' },
  { leading: true, size: 'lg' as const, class: 'ps-11' },
  { leading: true, size: 'xl' as const, class: 'ps-12' },
  { trailing: true, size: 'xs' as const, class: 'pe-8' },
  { trailing: true, size: 'sm' as const, class: 'pe-9' },
  { trailing: true, size: 'md' as const, class: 'pe-10' },
  { trailing: true, size: 'lg' as const, class: 'pe-11' },
  { trailing: true, size: 'xl' as const, class: 'pe-12' },
]

export default defineAppConfig({
  siteName: 'Foodlista',
  ui: {
    colors: {
      primary: 'rose',
      neutral: 'zinc',
    },
    icons: {
      loading: 'i-tabler-loader-2',
    },
    button: {
      defaultVariants: {
        size: 'lg',
      },
      compoundVariants: [
        {
          color: 'neutral',
          variant: 'solid',
          class: 'text-default bg-elevated hover:bg-accented active:bg-accented disabled:bg-elevated aria-disabled:bg-elevated',
        },
        // v2 size parity: square (icon-only) padding.
        { square: true, size: 'xs', class: 'p-1.5' },
        { square: true, size: 'md', class: 'p-2' },
        { square: true, size: 'lg', class: 'p-2.5' },
        { square: true, size: 'xl', class: 'p-2.5' },
      ],
      variants: {
        // v2 size parity.
        size: {
          xs: { base: 'px-2.5 py-1.5 gap-1.5' },
          sm: { base: 'text-sm', leadingIcon: 'size-5', trailingIcon: 'size-5' },
          md: { base: 'px-3 py-2 gap-2' },
          lg: { base: 'px-3.5 py-2.5 gap-2.5' },
          xl: { base: 'px-3.5 py-2.5 gap-2.5' },
        },
        block: {
          true: {
            base: 'shrink',
            // Nuxt UI's block variant sets `ms-auto` on the trailing icon; keep label + icon centered together.
            trailingIcon: 'ms-0',
          },
        },
      },
    },
    badge: {
      // v2 size parity: v4's `xs` badge has 8px text, v2's had 12px.
      variants: {
        size: {
          xs: { base: 'px-1.5 py-0.5 text-xs rounded-md' },
        },
      },
    },
    chip: {
      // v2 size parity.
      variants: {
        size: {
          lg: 'h-3 min-w-3 p-0.5 text-[10px]',
          xl: 'h-3.5 min-w-3.5 p-1 text-[11px]',
        },
      },
    },
    switch: {
      // v2 size parity: v2 `sm`/`lg` toggles match v4 `xs`/`xl` switches.
      variants: {
        size: {
          sm: {
            base: 'w-7',
            thumb: 'size-3 data-[state=checked]:translate-x-3 data-[state=checked]:rtl:-translate-x-3',
          },
          lg: {
            base: 'w-11',
            container: 'h-6',
            thumb: 'size-5 data-[state=checked]:translate-x-5 data-[state=checked]:rtl:-translate-x-5',
          },
        },
      },
    },
    input: {
      variants: { size: v2FieldSizeVariants }, // v2 size parity
      compoundVariants: v2FieldCompoundVariants,
      slots: {
        root: 'w-full',
        base: 'text-sm',
      },
      defaultVariants: {
        size: 'lg',
      },
    },
    inputMenu: {
      variants: { size: v2FieldSizeVariants }, // v2 size parity
      compoundVariants: v2FieldCompoundVariants,
      slots: {
        root: 'w-full',
      },
      defaultVariants: {
        size: 'lg',
      },
    },
    textarea: {
      slots: {
        root: 'w-full',
      },
    },
    select: {
      variants: { size: v2FieldSizeVariants }, // v2 size parity
      compoundVariants: v2FieldCompoundVariants,
      slots: {
        base: 'w-full',
      },
      defaultVariants: {
        size: 'lg',
      },
    },
    selectMenu: {
      variants: { size: v2FieldSizeVariants }, // v2 size parity
      compoundVariants: v2FieldCompoundVariants,
      slots: {
        base: 'w-full',
      },
      defaultVariants: {
        size: 'lg',
      },
    },
    modal: {
      slots: {
        content: 'max-h-full overflow-y-auto rounded-xl p-0 md:max-h-[60vh]',
        body: 'p-0 pt-24',
      },
    },
    accordion: {
      slots: {
        item: 'px-3 py-4',
      },
    },
    alert: {
      slots: {
        description: 'mt-1 text-sm leading-5 opacity-90',
      },
    },
  },
  // https://github.com/nuxt-modules/icon/issues/117
  nuxtIcon: {},
})
